import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Scatter,
  ComposedChart,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Metric } from "../../types/metrics";
import { callMetricsService } from "../../services/callMetricsService";

interface LatencyTimelineProps {
  metrics: Metric[];
}

interface MetricToggle {
  key: string;
  label: string;
  color: string;
  enabled: boolean;
}

export const LatencyTimeline: React.FC<LatencyTimelineProps> = ({ metrics }) => {
  // Metric toggles for showing/hiding lines
  const [metricToggles, setMetricToggles] = useState<MetricToggle[]>([
    {
      key: "stt_duration",
      label: "STT Duration",
      color: callMetricsService.getMetricColor("stt"),
      enabled: true,
    },
    {
      key: "llm_ttft",
      label: "LLM TTFT ⚡",
      color: callMetricsService.getMetricColor("llm"),
      enabled: true,
    },
    {
      key: "tts_ttfb",
      label: "TTS TTFB ⚡",
      color: callMetricsService.getMetricColor("tts"),
      enabled: true,
    },
    {
      key: "overall_response",
      label: "Overall Response",
      color: callMetricsService.getMetricColor("overall"),
      enabled: true,
    },
  ]);

  // Custom dot for scatter points to make them more prominent
  const CustomDot = (props: any) => {
    const { cx, cy, fill, value } = props;
    if (!value) return null;

    return (
      <g>
        {/* Outer glow circle */}
        <circle cx={cx} cy={cy} r={8} fill={fill} opacity={0.2} />
        {/* Main dot */}
        <circle cx={cx} cy={cy} r={5} fill={fill} stroke="white" strokeWidth={2} />
        {/* Inner highlight */}
        <circle cx={cx} cy={cy} r={2} fill="white" opacity={0.8} />
      </g>
    );
  };

  // Transform metrics to chart data
  const chartData = useMemo(() => {
    return callMetricsService.transformMetricsToChartData(metrics);
  }, [metrics]);

  // Handle toggle change
  const handleToggle = (key: string) => {
    setMetricToggles((prev) =>
      prev.map((toggle) =>
        toggle.key === key ? { ...toggle, enabled: !toggle.enabled } : toggle
      )
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Get the full data point to access token information
      const dataPoint = payload[0]?.payload;

      return (
        <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[250px]">
          <p className="font-medium mb-2 border-b pb-2">
            Call Time: {callMetricsService.formatRelativeTime(label)}
          </p>

          {/* Latency metrics */}
          <div className="space-y-1 mb-2">
            {payload.map((entry: any, index: number) => {
              // Check if this is a critical first token/byte metric
              const isCriticalMetric = entry.dataKey === "llm_ttft" || entry.dataKey === "tts_ttfb";

              return (
                <div key={index} className="flex items-center justify-between gap-4">
                  <span style={{ color: entry.color }} className="flex items-center gap-1">
                    {entry.name}
                    {isCriticalMetric && <span className="text-yellow-500">⚡</span>}:
                  </span>
                  <span className="font-mono font-medium">
                    {callMetricsService.formatDuration(entry.value)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* LLM Token Details - show if LLM data is present */}
          {dataPoint?.llm_total_tokens && (
            <div className="border-t pt-2 mt-2">
              <p className="text-xs font-semibold text-purple-500 mb-1">
                LLM Token Details:
              </p>
              <div className="space-y-0.5 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Input:</span>
                  <span className="font-mono">
                    {dataPoint.llm_prompt_tokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Output:</span>
                  <span className="font-mono">
                    {dataPoint.llm_completion_tokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-mono font-semibold">
                    {dataPoint.llm_total_tokens?.toLocaleString()}
                  </span>
                </div>
                {dataPoint.llm_cached_tokens > 0 && (
                  <div className="flex items-center justify-between gap-3 text-green-600">
                    <span>Cached:</span>
                    <span className="font-mono">
                      {dataPoint.llm_cached_tokens?.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 border-t pt-1 mt-1">
                  <span className="text-muted-foreground">Speed:</span>
                  <span className="font-mono">
                    {dataPoint.llm_tokens_per_second?.toFixed(1)} tok/s
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Latency Timeline</CardTitle>
          <CardDescription>No timeline data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Latency Timeline</CardTitle>
        <CardDescription>
          Real-time latency measurements throughout the call. ⚡ Dots highlight critical first token/byte moments (TTFT & TTFB).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Metric Toggles */}
        <div className="flex flex-wrap gap-4 mb-6">
          {metricToggles.map((toggle) => (
            <div key={toggle.key} className="flex items-center space-x-2">
              <Checkbox
                id={toggle.key}
                checked={toggle.enabled}
                onCheckedChange={() => handleToggle(toggle.key)}
              />
              <Label
                htmlFor={toggle.key}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center"
              >
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: toggle.color }}
                />
                {toggle.label}
              </Label>
            </div>
          ))}
        </div>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(seconds) =>
                callMetricsService.formatRelativeTime(seconds)
              }
              className="text-xs"
              label={{
                value: "Call Duration",
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              tickFormatter={(value) => callMetricsService.formatDuration(value)}
              className="text-xs"
              label={{
                value: "Latency",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />

            {metricToggles.find((t) => t.key === "stt_duration")?.enabled && (
              <Line
                type="monotone"
                dataKey="stt_duration"
                stroke={callMetricsService.getMetricColor("stt")}
                name="STT Duration"
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            )}

            {metricToggles.find((t) => t.key === "llm_ttft")?.enabled && (
              <>
                <Line
                  type="monotone"
                  dataKey="llm_ttft"
                  stroke={callMetricsService.getMetricColor("llm")}
                  name="LLM TTFT ⚡"
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
                {/* Highlight LLM TTFT points with prominent dots */}
                <Scatter
                  dataKey="llm_ttft"
                  fill={callMetricsService.getMetricColor("llm")}
                  shape={<CustomDot />}
                  legendType="none"
                />
              </>
            )}

            {metricToggles.find((t) => t.key === "tts_ttfb")?.enabled && (
              <>
                <Line
                  type="monotone"
                  dataKey="tts_ttfb"
                  stroke={callMetricsService.getMetricColor("tts")}
                  name="TTS TTFB ⚡"
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
                {/* Highlight TTS TTFB points with prominent dots */}
                <Scatter
                  dataKey="tts_ttfb"
                  fill={callMetricsService.getMetricColor("tts")}
                  shape={<CustomDot />}
                  legendType="none"
                />
              </>
            )}

            {metricToggles.find((t) => t.key === "overall_response")?.enabled && (
              <Line
                type="monotone"
                dataKey="overall_response"
                stroke={callMetricsService.getMetricColor("overall")}
                name="Overall Response"
                dot={false}
                strokeWidth={2}
                strokeDasharray="5 5"
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
